import { ADLE_APP_BASE_URL } from "./constants";
import { sortNotificationsByRecency } from "./notifications";

export const GLOBAL_NOTIFICATION_TYPE = "globalNotification";
export const GLOBAL_NOTIFICATION_RTDB_SOURCE = "globalNotificationRtdb";
export const GLOBAL_NOTIFICATIONS_RTDB_PATH = "globalNotifications";

export const getGlobalNotificationsUserPath = (uid) =>
  `globalNotificationsByUsers/${uid}`;

const MAX_TITLE_LENGTH = 120;
const MAX_BODY_LENGTH = 2000;
const MAX_CTA_LENGTH = 60;
const MAX_LINK_LENGTH = 2000;

export const isGlobalNotification = (notification) =>
  notification?.type === GLOBAL_NOTIFICATION_TYPE ||
  notification?.source === GLOBAL_NOTIFICATION_RTDB_SOURCE;

export const isValidGlobalNotificationLink = (rawLink) => {
  const link = String(rawLink || "").trim();
  if (!link || link.length > MAX_LINK_LENGTH) {
    return false;
  }

  try {
    const url = new URL(link);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

// The backend only accepts links to the Adle app domain (app.adle.ai).
export const isAdleAppGlobalNotificationLink = (rawLink) => {
  try {
    return (
      new URL(String(rawLink || "").trim()).host.toLowerCase() ===
      new URL(ADLE_APP_BASE_URL).host.toLowerCase()
    );
  } catch {
    return false;
  }
};

const trimToMax = (value, maxLength) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const sanitizeLocaleContent = (raw) => ({
  title: trimToMax(raw?.title, MAX_TITLE_LENGTH),
  body: trimToMax(raw?.body, MAX_BODY_LENGTH),
  cta: trimToMax(raw?.cta, MAX_CTA_LENGTH),
});

const sanitizeI18n = (raw) => {
  if (!raw || typeof raw !== "object") {
    return {};
  }

  return Object.entries(raw).reduce((locales, [code, content]) => {
    if (!code || !content || typeof content !== "object") {
      return locales;
    }

    locales[code] = sanitizeLocaleContent(content);
    return locales;
  }, {});
};

const hasVisibleCopy = (i18n) =>
  Object.values(i18n).some((content) => content.title && content.body);

export const resolveLocalizedGlobalNotification = (notification, language) => {
  const locale = String(language || "en")
    .split("-")[0]
    .toLowerCase();
  const i18n = notification?.i18n || {};
  const preferred = i18n[locale] || {};
  const fallback = i18n.en || {};

  return {
    title: preferred.title || fallback.title || "",
    body: preferred.body || fallback.body || "",
    cta: preferred.cta || fallback.cta || "",
    link: String(notification?.link || "").trim(),
  };
};

const readUserFlags = (userValue) => {
  const readIds = [];
  const dismissedIds = [];

  if (!userValue || typeof userValue !== "object") {
    return { readIds, dismissedIds };
  }

  Object.entries(userValue).forEach(([id, flags]) => {
    if (!id || !flags || typeof flags !== "object") {
      return;
    }
    if (flags.read === true) {
      readIds.push(id);
    }
    if (flags.dismissed === true) {
      dismissedIds.push(id);
    }
  });

  return { readIds, dismissedIds };
};

export const applyGlobalNotificationPending = (
  alerts,
  pendingReadIds = [],
  pendingDismissedIds = []
) => {
  const dismissed = new Set(pendingDismissedIds);
  const read = new Set(pendingReadIds);

  return (Array.isArray(alerts) ? alerts : [])
    .filter((alert) => alert?.id && !dismissed.has(alert.id))
    .map((alert) => (read.has(alert.id) ? { ...alert, isRead: true } : alert));
};

export const mapGlobalNotificationsToAlerts = ({
  notificationsValue,
  userValue,
} = {}) => {
  const { readIds, dismissedIds } = readUserFlags(userValue);
  const dismissed = new Set(dismissedIds);
  const read = new Set(readIds);

  if (!notificationsValue || typeof notificationsValue !== "object") {
    return { alerts: [], readIds, dismissedIds };
  }

  const mapped = Object.entries(notificationsValue)
    .filter(([id, data]) => {
      if (!id || dismissed.has(id) || !data || typeof data !== "object") {
        return false;
      }

      return hasVisibleCopy(sanitizeI18n(data.i18n));
    })
    .map(([id, data]) => {
      const creationTimeRaw = Number(data.creationTime);
      const timestamp = Number.isFinite(creationTimeRaw)
        ? creationTimeRaw
        : Date.now();

      return {
        id,
        type: GLOBAL_NOTIFICATION_TYPE,
        source: GLOBAL_NOTIFICATION_RTDB_SOURCE,
        status: "success",
        timestamp,
        isRead: read.has(id),
        i18n: sanitizeI18n(data.i18n),
        link: String(data.link || "").trim(),
      };
    });

  return {
    alerts: sortNotificationsByRecency(mapped),
    readIds,
    dismissedIds,
  };
};
